/**
 * Case plan create service.
 * Wraps portal create-plan read APIs and normalizes responses for the UI.
 */

import { USER_CONTEXT } from '../../../../constants';
import {
  createCasePlans,
  fetchCasePlanTemplates,
  searchCasePlanResidents,
  searchCasePlanStaffEditors,
} from '../api/casePlanCreateApi';
import {
  mapEditorSearchItem,
  mapResidentSearchItem,
  mapTemplateListItem,
  RESIDENT_PLAN_TYPE,
  TEMPLATE_PLAN_TYPE,
} from '../utils/case-plan-create.util';

const PLAN_TYPES = [RESIDENT_PLAN_TYPE, TEMPLATE_PLAN_TYPE];

/**
 * Resolves the logged-in user's display name from USER_CONTEXT.
 * @returns {string}
 */
const resolveCurrentUserName = () => USER_CONTEXT?.name || '';

/**
 * Returns available plan types for the create form.
 * @returns {Promise<Array<{key: string, name: string, description: string}>>}
 */
export const fetchPlanTypes = async () => [...PLAN_TYPES];

/**
 * Searches residents eligible for a new plan.
 * Residents with an existing plan are included but marked hasPlan: true.
 * @param {string} query
 * @param {AbortSignal} [signal]
 * @returns {Promise<{results: Array}>}
 */
export const searchResidents = async (query, signal) => {
  const response = await searchCasePlanResidents(
    { q: (query || '').trim() },
    signal,
  );
  const results = (response?.items || [])
    .map(mapResidentSearchItem)
    .filter(Boolean);

  return { results };
};

/**
 * Returns available plan templates from the portal API.
 * @param {string} [query]
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<{id: string, name: string}>>}
 */
export const fetchPlanTemplates = async (query, signal) => {
  const response = await fetchCasePlanTemplates({ q: query }, signal);
  return (response?.items || []).map(mapTemplateListItem).filter(Boolean);
};

/**
 * Searches staff who can be added as plan editors (case_plan_reentry_edit).
 * @param {string} query
 * @param {AbortSignal} [signal]
 * @returns {Promise<{results: Array}>}
 */
export const searchEditors = async (query, signal) => {
  const response = await searchCasePlanStaffEditors(
    { q: (query || '').trim() },
    signal,
  );
  const results = (response?.items || [])
    .map(mapEditorSearchItem)
    .filter(Boolean);

  return { results };
};

/**
 * Returns the current user (default plan creator / editor) from USER_CONTEXT.
 * @returns {Promise<{personId: number|string|undefined, name: string, username: string, isCreator: boolean}>}
 */
export const fetchCurrentUser = async () => ({
  personId: USER_CONTEXT?.personId,
  name: resolveCurrentUserName(),
  username: USER_CONTEXT?.preferred_username || USER_CONTEXT?.username || '',
  isCreator: true,
});

/**
 * Creates case plan(s) via POST /api/admin/case-plan (CC-14978 host).
 * @param {{ planType: string, learnerIds: number[], editorIds?: number[], templateId?: string }} payload
 * @returns {Promise<{ results: Array }>}
 */
export const createCasePlan = async (payload) => createCasePlans(payload);
